import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaModal,
  VaRadio,
  VaAlert,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import _ from 'platform/utilities/data';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import { scrollToFirstError } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import { DATA_PATHS } from '../constants';
import { checkValidations } from '../../shared/utils/validations';
import { getAdditionalDocuments } from '../utils';
import { renderFileList } from '../content/evidenceRequest';
import {
  evidenceChoiceIntroDescriptionContent,
  evidenceChoiceIntroAccordionContent,
  evidenceChoiceIntroQuestion,
  evidenceChoiceIntroTitle,
  evidenceChoiceIntroTitleAlt,
  evidenceChoiceIntroHintAlt,
  additionalEvidenceModalContent,
  missingSelectionErrorMessage,
} from '../../shared/content/evidenceChoiceIntroPage';

export const AdditionalEvidenceIntroPage = ({
  data,
  setFormData,
  onReviewPage,
  contentBeforeButtons,
  contentAfterButtons,
  goBack,
  goForward,
  updatePage,
}) => {
  const additionalEvidenceSelected = _.get(
    DATA_PATHS.hasAdditionalDocuments,
    data,
    null,
  );
  const [hasError, setHasError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const hasEvidenceToRemove = () => {
    return getAdditionalDocuments(data).length > 0;
  };

  const mapFormDataToLegacy = (formData, selection) => {
    const selectableEvidenceTypes =
      formData['view:selectableEvidenceTypes'] || {};
    const hasVaMedicalRecords =
      selectableEvidenceTypes['view:hasVaMedicalRecords'] === true;
    const hasPrivateMedicalRecords =
      selectableEvidenceTypes['view:hasPrivateMedicalRecords'] === true;
    const medicalRecordsAnswer = formData['view:hasMedicalRecords'];

    const hasAnyEvidence =
      selection ||
      hasVaMedicalRecords ||
      hasPrivateMedicalRecords ||
      medicalRecordsAnswer === true;

    if (hasAnyEvidence) {
      return true;
    }

    // No positive evidence signal. The medical-records question
    // (view:hasMedicalRecords) is a *separate* question from this one — if it
    // hasn't been answered yet, don't let answering the other-evidence question
    // pull view:hasEvidence down to false: downstream, view:hasEvidence ===
    // false reads as an unambiguous "no evidence at all" (see
    // deriveMedicalRecordsFromLegacyData), which drops the medical-records
    // required check and lets the Veteran submit it unanswered. Preserve the
    // existing value until medical records is actually answered.
    if (medicalRecordsAnswer === undefined) {
      return formData['view:hasEvidence'];
    }

    return false;
  };

  useEffect(() => {
    if (hasError) {
      scrollToFirstError();
    }
  }, [hasError]);
  const missingSelection = (error, _fieldData, formData) => {
    const value =
      formData?.['view:selectableEvidenceTypes']?.['view:hasOtherEvidence'];
    if (value !== true && value !== false) {
      error.addError?.(missingSelectionErrorMessage);
    }
  };

  const checkErrors = (formData = data) => {
    const error = checkValidations(
      [missingSelection],
      data?.['view:selectableEvidenceTypes']?.['view:hasOtherEvidence'],
      formData,
    );

    const result = error?.[0] || null;
    setHasError(result);

    return result;
  };

  const handlers = {
    onChangeAndRemove: () => {
      const updatedFormData = { ...data };
      delete updatedFormData.additionalDocuments;
      setFormData(updatedFormData);
      setModalVisible(false);
      /**
        thread for reason to use setTimeout here
        @see https://dsva.slack.com/archives/C01DBGX4P45/p1774282809644219
      */
      setTimeout(() => {
        // focus on the 'continue' button
        focusElement('.usa-button-primary');
        // followed by setting the alert to be visible
        setAlertVisible(true);
      }, 100);
    },
    onCancelChange: () => {
      const updatedFormData = {
        ...data,
        'view:selectableEvidenceTypes': {
          ...(data['view:selectableEvidenceTypes'] || {}),
          'view:hasOtherEvidence': true,
        },
        'view:hasEvidence': true,
      };
      setFormData(updatedFormData);
      setModalVisible(false);
    },
    onSelection: event => {
      const { value } = event?.detail || {};
      const booleanValue = value === true || value === 'true';
      const formData = {
        ...data,
        'view:selectableEvidenceTypes': {
          ...(data['view:selectableEvidenceTypes'] || {}),
          'view:hasOtherEvidence': booleanValue,
        },
        'view:hasEvidence': mapFormDataToLegacy(data, booleanValue),
      };
      setFormData(formData);
      checkErrors(formData);
    },
    onSubmit: event => {
      event.preventDefault();
      if (checkErrors()) {
        return;
      }
      if (additionalEvidenceSelected === false && hasEvidenceToRemove()) {
        setModalVisible(true);
      } else {
        setAlertVisible(false);
        goForward({ formData: data });
      }
    },
    onUpdatePage: event => {
      event.preventDefault();
      if (checkErrors()) {
        return;
      }
      if (additionalEvidenceSelected === false && hasEvidenceToRemove()) {
        setModalVisible(true);
      } else {
        setAlertVisible(false);
        updatePage(event);
      }
    },
  };

  return (
    <>
      <h3>
        {data?.disabilityCompConditionsEvidenceMessagingTest
          ? evidenceChoiceIntroTitleAlt
          : evidenceChoiceIntroTitle}
      </h3>
      {!data?.disabilityCompConditionsEvidenceMessagingTest &&
        evidenceChoiceIntroDescriptionContent}
      {alertVisible && (
        <div className="vads-u-margin-bottom--1">
          <VaAlert
            closeBtnAriaLabel="Close notification"
            role="alert"
            closeable
            onCloseEvent={() => setAlertVisible(false)}
            fullWidth="false"
            slim
            status="success"
            uswds
          >
            <p className="vads-u-margin-y--0">
              We’ve deleted the documents you uploaded supporting your claim.
            </p>
          </VaAlert>
        </div>
      )}
      <VaModal
        clickToClose
        modalTitle="Cancel uploading files?"
        onCloseEvent={handlers.onCancelChange}
        onPrimaryButtonClick={handlers.onChangeAndRemove}
        onSecondaryButtonClick={handlers.onCancelChange}
        visible={modalVisible}
        status="warning"
        primaryButtonText="Cancel and delete"
        secondaryButtonText="Keep files"
      >
        {getAdditionalDocuments(data).length > 0 && (
          <>
            {additionalEvidenceModalContent}
            {renderFileList(getAdditionalDocuments(data), true)}
          </>
        )}
      </VaModal>
      <form onSubmit={handlers.onSubmit}>
        <VaRadio
          label={evidenceChoiceIntroQuestion}
          required
          uswds="true"
          class="rjsf-web-component-field hydrated"
          aria-invalid={hasError ? 'true' : 'false'}
          onVaValueChange={handlers.onSelection}
          error={hasError}
        >
          {data?.disabilityCompConditionsEvidenceMessagingTest && (
            <p className="vads-u-color--gray vads-u-margin-top--2 vads-u-margin-bottom--0">
              {evidenceChoiceIntroHintAlt}
            </p>
          )}
          <va-radio-option
            label="Yes"
            name="private"
            checked={additionalEvidenceSelected === true}
            value="true"
          />
          <va-radio-option
            label="No"
            name="private"
            checked={additionalEvidenceSelected === false}
            value="false"
          />
        </VaRadio>
        {data?.disabilityCompConditionsEvidenceMessagingTest &&
          evidenceChoiceIntroAccordionContent}
        {onReviewPage ? (
          /**
           * Does not use web component for design consistency on all pages.
           * @see https://va.ghe.com/software/vets-website/pull/35911
           */
          // eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component
          <button
            className="usa-button-primary"
            type="button"
            onClick={event => handlers.onUpdatePage(event)}
          >
            Update page
          </button>
        ) : (
          <>
            {contentBeforeButtons}
            <FormNavButtons
              goBack={goBack}
              goForward={handlers.onSubmit}
              submitToContinue
              useWebComponents
              enableAnalytics
            />
            {contentAfterButtons}
          </>
        )}
      </form>
    </>
  );
};

AdditionalEvidenceIntroPage.propTypes = {
  contentAfterButtons: PropTypes.element,
  contentBeforeButtons: PropTypes.element,
  data: PropTypes.object,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  setFormData: PropTypes.func,
  updatePage: PropTypes.func,
  onReviewPage: PropTypes.bool,
};
